import React, { useEffect, useState, useRef } from "react";
import "./recentBAGsDropdown.css";
import { useLazyQuery, useMutation } from "@apollo/client";
import { GET_RECENT_CREATED_BAGS } from "../../../../utils/GraphQL/queries";
import { DELETE_BA_GROUP } from "../../../../utils/GraphQL/mutations";
import BdrDisclosure from "../../../../components/BdrDisclosure/BdrDisclosure";
import PhotoLibraryIcon from "@mui/icons-material/PhotoLibrary";
import { Button, useMediaQuery } from "@mui/material";
import AdminBDRSpinner from "../AdminBDRSpinner/AdminBDRSpinner";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import ImgGroupModal from "../../../../components/Modal/imgGroupModal";
import { baImageUrl } from "../../../../utils/baImageUrl";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import BlockIcon from "@mui/icons-material/Block";

const RecentBAGsDropdown = ({ inPanel = false }) => {
  const [loading, setLoading] = useState(false);
  const [barCollapsed, setBarCollapsed] = useState(true);
  const smaller900 = useMediaQuery("(max-width:900px)");
  const { handleSnack, handleClose: handleSnackClose } = useSnackbar();
  const [getBAGs] = useLazyQuery(GET_RECENT_CREATED_BAGS);
  const [imgData, setImgData] = useState([]);
  const recentBAGsScrollRef = useRef();
  const [modalGroupArrId, setModalGroupArrId] = useState(Number);
  const [imgModalOpen, setImgModalOpen] = useState(false);
  const [deleteBAGCall] = useMutation(DELETE_BA_GROUP);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (imgData?.length === 0 && barCollapsed === false) {
      setLoading(true);
      getRecentBAGs();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [barCollapsed]);

  const getRecentBAGs = async () => {
    const { data } = await getBAGs({
      fetchPolicy: "network-only",
    });

    setImgData([...data?.getRecentCreatedBAGs]);
    setLoading(false);
  };

  // modal controls
  const handleImgOpen = () => setImgModalOpen(true);
  const handleImgClose = () => setImgModalOpen(false);

  const imgModalEvent = (event) => {
    handleImgOpen();
    if (event.target.id === "") {
      setModalGroupArrId(event.target.parentNode.id);
      return;
    }

    setModalGroupArrId(event.target.id);
  };

  const deleteBAG = async (id) => {
    const { data } = await deleteBAGCall({
      variables: {
        baGroupId: id,
      },
    });

    if (data?.deleteBAGroup === true) {
      handleSnack("Group Deleted", 2000);
      setLoading(true);
      getRecentBAGs();
      return;
    }

    handleSnack("Something went wrong...", 2500);
  };

  const deleteBAGSnack = (id) => {
    handleSnack(
      <div className="recent-bags-snack">
        <span>Delete Image Group?</span>
        <button
          className="recent-bags-action-img-button"
          onClick={(event) => {
            event.stopPropagation();
            deleteBAG(id);
            handleSnackClose();
          }}
        >
          <CheckIcon
            sx={{ fontSize: "20px", color: "#05F842" }}
            titleAccess="Yes"
          />
        </button>
        <button
          className="recent-bags-action-img-button"
          onClick={(event) => {
            event.stopPropagation();
            handleSnackClose();
          }}
        >
          <BlockIcon
            sx={{ fontSize: "20px", color: "#c80404" }}
            titleAccess="No"
          />
        </button>
      </div>,
      6000
    );
  };

  return (
    <div
      id="admin-pic-lookup-height-marker"
      className={inPanel ? "" : "bdr-v2-admin-container"}
      ref={recentBAGsScrollRef}
    >
      {loading && <AdminBDRSpinner message={"Loading..."} />}
      {imgModalOpen ? (
        <ImgGroupModal
          BAGroup={imgData[modalGroupArrId]}
          handleImgClose={handleImgClose}
          imgModalOpen={imgModalOpen}
        />
      ) : null}
      <BdrDisclosure
        bare={inPanel}
        open={!barCollapsed}
        label="Recent BAGs"
        icon={<PhotoLibraryIcon />}
        count={imgData?.length}
        emptyText="No recent groups"
        onToggle={() => {
          setTimeout(() => {
            if (smaller900 === true && barCollapsed === true) {
              const navHeight = document.querySelector(
                "#admin-nav-height-marker"
              )?.offsetHeight;
              const pagePos = recentBAGsScrollRef.current.offsetTop;

              const totalMobileScroll = pagePos - navHeight - 5;
              window.scrollTo(0, totalMobileScroll);
            }

            if (smaller900 === false && barCollapsed === true) {
              recentBAGsScrollRef.current.scrollIntoView({ block: "start" });
            }

            if (barCollapsed === false) {
              window.scrollTo({ top: 0 });
            }
          }, 500);

          setBarCollapsed((prev) => !prev);
        }}
      >
        <div className="bdr-v2-content">
          {imgData?.length > 0 && (
            <div className="recent-bags-gallery">
              {imgData?.map((singleGroupData, index) => {
                return (
                  <div
                    className="recent-bags-images-box"
                    key={singleGroupData?._id + index}
                  >
                    <Button
                      className="recent-bags-img-box"
                      id={index}
                      onClick={imgModalEvent}
                    >
                      <img
                        className="recent-bags-img"
                        src={
                          singleGroupData?.beforeId?.s3ThumbUrl ??
                          baImageUrl(singleGroupData?.beforeId?._id, {
                            variant: "thumb",
                          })
                        }
                        alt={"Door Project Before" + (index + 1)}
                      />
                      <img
                        className="recent-bags-img"
                        src={
                          singleGroupData?.afterId?.s3ThumbUrl ??
                          baImageUrl(singleGroupData?.afterId?._id, {
                            variant: "thumb",
                          })
                        }
                        alt={"Door Project After" + (index + 1)}
                      />
                    </Button>
                    <span className="recent-bags-stain-hdr">
                      {singleGroupData?.stainColor}
                    </span>
                    <span className="recent-bags-stain-hdr">
                      {BAGroupTimeFormat(
                        singleGroupData?.dateEntered,
                        false,
                        true
                      )}{" "}
                      -{""}
                      <button
                        className="recent-bags-action-img-button"
                        onClick={(event) => {
                          event.stopPropagation();
                          deleteBAGSnack(singleGroupData?._id);
                        }}
                      >
                        <DeleteIcon
                          titleAccess="Delete"
                          sx={{ fontSize: "13px" }}
                        />
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </BdrDisclosure>
    </div>
  );
};

export default RecentBAGsDropdown;
